import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { parseAtlasAnalysis, type AtlasLoadAnalysis } from "@/components/lr-direct/AtlasLoadAnalysisCard";

/**
 * Self-heal for ATLAS Load Analysis.
 *
 * Analysis is normally generated once when the shipper posts. Some post paths
 * (voice post, instant template post, bulk upload) or a transient failure can
 * leave a bid load without it. When a broker opens such a load we generate it
 * once, then read the freshly stored row. Cached loads never trigger a call.
 */
export function useEnsureAtlasLoadAnalysis(
  loadId: string | undefined,
  existing: AtlasLoadAnalysis | null,
  enabled: boolean,
): AtlasLoadAnalysis | null {
  const [generated, setGenerated] = useState<AtlasLoadAnalysis | null>(null);

  useEffect(() => {
    setGenerated(null);
  }, [loadId]);

  useEffect(() => {
    if (!enabled || !loadId || existing) return;
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("atlas-direct-load-analysis", {
          body: { direct_load_id: loadId },
        });
        if (cancelled || error) return;
        const fromFn = parseAtlasAnalysis((data as any)?.analysis);
        if (fromFn) {
          setGenerated(fromFn);
          return;
        }
        const { data: row } = await supabase
          .from("direct_loads")
          .select("atlas_analysis")
          .eq("id", loadId)
          .maybeSingle();
        if (cancelled) return;
        const stored = parseAtlasAnalysis((row as any)?.atlas_analysis);
        if (stored) setGenerated(stored);
      } catch {
        /* best effort — card simply stays hidden */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, loadId, existing]);

  return existing ?? generated;
}
